import * as THREE from 'three/webgpu';
import { AssemblyTransitionState } from './AssemblyTransitionState';
import { GeometryInteractionState } from './AbstractInteractionVisualState';
import type { InteractionVisualStateContext, InteractionVisualStateRuntime } from './AbstractInteractionVisualState';
import type { Viewer } from '../../viewer/Viewer';
import { ImportedModelInstancedSelectionOverlayManager } from '../../imported-model/instancing/selection';

/**
 * 线性零部件阵列视觉状态。
 *
 * 设计说明：
 * 1. 线性零部件阵列属于装配体命令，复用装配体 imported-model 高亮和包围盒语义；
 * 2. 针对“要阵列的零部件”槽位，普通 Mesh 继续替换高亮材质，实例 source Mesh 使用单槽覆盖；
 * 3. 方向参考对象高亮已由 LinearComponentPatternEntitySelectionHandler 通过
 *    updateSelectionState 写入标准状态机，无需在此重复处理。
 */
export class LinearComponentPatternTransitionState extends AssemblyTransitionState {
    /** “要阵列的零部件”实例 source Mesh 单槽选中覆盖管理器。 */
    private readonly instancedSourceSelectionOverlayManager: ImportedModelInstancedSelectionOverlayManager;

    /**
     * 创建线性零部件阵列视觉状态。
     *
     * @param runtime 交互视觉状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
        this.instancedSourceSelectionOverlayManager = new ImportedModelInstancedSelectionOverlayManager(
            (sourceMesh) => this.viewer.resolveImportedModelInstancedMeshUnit(sourceMesh),
            () => this.getImportedModelTreeSelectedBodyMaterial(),
        );
    }

    /**
     * 刷新视觉状态。
     *
     * 重写说明：
     * 在父类处理完装配体通用高亮后，追加“要阵列的零部件”的 mesh 高亮。
     * 零件是 THREE.Group，默认状态机不识别 Group 级对象，必须下探到普通或实例 source Mesh。
     *
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        super.refreshVisualState(context);
        this.applyComponentsToPatternHighlight();
    }

    /**
     * 重置线性阵列命令态额外创建的实例覆盖对象。
     */
    public override resetVisualState(): void {
        this.instancedSourceSelectionOverlayManager.reset();
        super.resetVisualState();
    }

    //------ 线性零部件阵列-零部件选中高亮 开始------
    /**
     * 为控制器中已选零部件的普通或实例 source Mesh 应用选中视觉。
     *
     * 设计说明：
     * 1. 从控制器读取 componentObjectIds，通过场景 UUID 查找对应对象；
     * 2. 普通 Mesh 通过 collectImportedVisibleMeshes 继续走材质快照和状态机；
     * 3. 被实例投影隐藏的 source Mesh 通过活动 Unit 创建精确单槽覆盖，不修改共享材质；
     * 4. resetVisualState 同时恢复普通材质并释放覆盖对象。
     */
    private applyComponentsToPatternHighlight(): void {
        const controller = this.viewer.linearComponentPatternController;
        const state = controller?.getViewModel?.().state;
        if (!state || state.componentObjectIds.length === 0) {
            this.instancedSourceSelectionOverlayManager.apply([]);
            return;
        }

        const scene = this.viewer.scene;
        const selectedMaterial = this.getImportedModelTreeSelectedMeshMaterial();
        const instancedSourceMeshes: THREE.Mesh[] = [];

        for (const objectId of state.componentObjectIds) {
            const rootObject = scene.getObjectByProperty('uuid', objectId);
            if (!rootObject) {
                continue;
            }

            /**
             * source Mesh 被正式 InstancedMesh 接管后会保持 visible=false，不能再依赖普通材质替换。
             * 收集精确 occurrence source Mesh 后统一创建单槽覆盖，避免高亮整个共享实例组。
             */
            rootObject.traverse((object) => {
                if (!(object instanceof THREE.Mesh)) {
                    return;
                }
                if (!this.viewer.resolveImportedModelInstancedMeshUnit(object)) {
                    return;
                }
                if (object.userData?.skipSelection || object.userData?.isImportedModelOverlay) {
                    return;
                }
                if (this.isImportedModelTopologyProxyObject(object)) {
                    return;
                }
                instancedSourceMeshes.push(object);
            });

            const visibleMeshes = this.collectImportedVisibleMeshes(rootObject);
            for (const mesh of visibleMeshes) {
                // 快照原材质，保证 resetVisualState 可以回退。
                this.snapshotOriginalMaterial(mesh);
                mesh.material = selectedMaterial;
                // 写入状态机，避免 hover 覆盖选中态。
                this.runtime.objectStateMachine.set(mesh.uuid, GeometryInteractionState.Selected);
            }
        }
        this.instancedSourceSelectionOverlayManager.apply(instancedSourceMeshes);
    }
    //------ 线性零部件阵列-零部件选中高亮 结束------
}
